// Theme + mode + lang + paint flag manager.
// Persists via the whitelisted preferences API and broadcasts via realtime.

import { storage } from "../utils/storage.js";
import { showReloadOverlay } from "./reload_overlay.js";

const VALID_ACCENTS = ["blue", "green", "red", "orange", "yellow", "pink", "violet"];
const VALID_MODES = ["light", "dark"];
const VALID_LANGS = ["en", "ar"];
const PAINT_KEYS = ["paint_menu", "paint_dashboard", "paint_workspace", "paint_navbar"];
const DEFAULTS = {
	accent: "green",
	mode: "light",
	lang: "en",
	paint_menu: 1, paint_dashboard: 1, paint_workspace: 1, paint_navbar: 1,
};

function isValidAccent(a) { return VALID_ACCENTS.includes(a); }
function isValidMode(m)   { return VALID_MODES.includes(m); }
function isValidLang(l)   { return VALID_LANGS.includes(l); }

function applyAccentToDom(accent) {
	if (!isValidAccent(accent)) accent = DEFAULTS.accent;
	document.documentElement.setAttribute("data-e16-accent", accent);
	storage.set("accent", accent);
	// Bridge to Frappe's data-theme attribute so internal components react.
	document.dispatchEvent(new CustomEvent("e16:accent-applied", { detail: { accent } }));
}

function applyModeToDom(mode) {
	if (!isValidMode(mode)) mode = DEFAULTS.mode;
	document.documentElement.setAttribute("data-e16-mode", mode);
	document.documentElement.setAttribute("data-theme-mode", mode);
	document.documentElement.setAttribute("data-theme", mode);
	storage.set("mode", mode);
	document.dispatchEvent(new CustomEvent("e16:mode-applied", { detail: { mode } }));
}

function applyLangToDom(lang) {
	if (!isValidLang(lang)) lang = DEFAULTS.lang;
	document.documentElement.setAttribute("lang", lang);
	document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
	storage.set("lang", lang);
	document.dispatchEvent(new CustomEvent("e16:lang-applied", { detail: { lang } }));
}

function applyPaintToDom(flags) {
	const merged = {
		paint_menu: flags.paint_menu ?? DEFAULTS.paint_menu,
		paint_dashboard: flags.paint_dashboard ?? DEFAULTS.paint_dashboard,
		paint_workspace: flags.paint_workspace ?? DEFAULTS.paint_workspace,
		paint_navbar: flags.paint_navbar ?? DEFAULTS.paint_navbar,
	};
	const html = document.documentElement;
	html.classList.toggle("e16-paint-menu", !!merged.paint_menu);
	html.classList.toggle("e16-paint-dashboard", !!merged.paint_dashboard);
	html.classList.toggle("e16-paint-workspace", !!merged.paint_workspace);
	html.classList.toggle("e16-paint-navbar", !!merged.paint_navbar);
	storage.set("paint", merged);
	document.dispatchEvent(new CustomEvent("e16:paint-applied", { detail: merged }));
}

export const theme = {
	VALID_ACCENTS, VALID_MODES, VALID_LANGS, DEFAULTS,

	currentAccent() {
		return document.documentElement.getAttribute("data-e16-accent") || DEFAULTS.accent;
	},
	currentMode() {
		return document.documentElement.getAttribute("data-e16-mode") || DEFAULTS.mode;
	},
	currentLang() {
		return document.documentElement.getAttribute("lang") === "ar" ? "ar" : "en";
	},
	currentPaint() {
		const html = document.documentElement;
		return {
			paint_menu: html.classList.contains("e16-paint-menu") ? 1 : 0,
			paint_dashboard: html.classList.contains("e16-paint-dashboard") ? 1 : 0,
			paint_workspace: html.classList.contains("e16-paint-workspace") ? 1 : 0,
			paint_navbar: html.classList.contains("e16-paint-navbar") ? 1 : 0,
		};
	},

	applyAccent(accent, { persist = true } = {}) {
		applyAccentToDom(accent);
		if (persist) this._persist({ accent });
	},
	applyMode(mode, { persist = true } = {}) {
		applyModeToDom(mode);
		if (persist) this._persist({ mode });
	},
	applyLang(lang, { persist = true, reload = false } = {}) {
		applyLangToDom(lang);
		if (persist && this._loggedIn()) {
			this._persistAndReload({ lang }, reload);
		} else if (reload) {
			this._reloadCacheBust();
		}
	},
	applyPaint(flags, { persist = true } = {}) {
		applyPaintToDom(flags);
		if (persist) this._persist(flags);
	},

	_loggedIn() {
		return window.frappe && frappe.session && frappe.session.user && frappe.session.user !== "Guest";
	},

	_persist(partial) {
		if (!this._loggedIn()) return;
		try {
			frappe.call({
				method: "erpnext16_theme.api.preferences.set_prefs",
				args: partial,
				freeze: false,
			}).catch(() => {});
		} catch (e) {}
	},

	_persistAndReload(partial, reload) {
		if (!this._loggedIn()) {
			if (reload) this._reloadCacheBust();
			return;
		}
		frappe.call({
			method: "erpnext16_theme.api.preferences.set_prefs",
			args: partial,
			freeze: false,
		}).then(() => {
			if (reload) this._reloadCacheBust();
		}).catch(() => {
			if (reload) this._reloadCacheBust();
		});
	},

	_reloadCacheBust() {
		showReloadOverlay({ cacheBust: true });
	},

	bootstrap() {
		// Server boot prefs > localStorage > default
		const serverPref = (window.frappe && frappe.boot && frappe.boot.e16_prefs) || {};
		const accent = isValidAccent(serverPref.accent) ? serverPref.accent : (storage.get("accent") || DEFAULTS.accent);
		const mode = isValidMode(serverPref.mode) ? serverPref.mode : (storage.get("mode") || DEFAULTS.mode);
		const lang = isValidLang(serverPref.lang) ? serverPref.lang : (storage.get("lang") || DEFAULTS.lang);
		const paint = {
			paint_menu: serverPref.paint_menu ?? storage.get("paint")?.paint_menu ?? DEFAULTS.paint_menu,
			paint_dashboard: serverPref.paint_dashboard ?? storage.get("paint")?.paint_dashboard ?? DEFAULTS.paint_dashboard,
			paint_workspace: serverPref.paint_workspace ?? storage.get("paint")?.paint_workspace ?? DEFAULTS.paint_workspace,
			paint_navbar: serverPref.paint_navbar ?? storage.get("paint")?.paint_navbar ?? DEFAULTS.paint_navbar,
		};

		applyAccentToDom(accent);
		applyModeToDom(mode);
		applyLangToDom(lang);
		applyPaintToDom(paint);

		// Cross-tab sync
		try {
			if (window.frappe && frappe.realtime) {
				frappe.realtime.on("e16:prefs-changed", (data) => {
					if (!data) return;
					if (data.accent && data.accent !== this.currentAccent()) applyAccentToDom(data.accent);
					if (data.mode && data.mode !== this.currentMode()) applyModeToDom(data.mode);
				});
				frappe.realtime.on("e16:settings-changed", (data) => {
					if (!data) return;
					const userHasAccent = window.frappe?.boot?.e16_prefs?.accent_set_by_user;
					if (!userHasAccent && data.default_accent) applyAccentToDom(data.default_accent);
				});
			}
		} catch (e) {}
	},
};
